import { useState, useEffect } from "react";

import { useAuthContext } from "../../../hooks/useAuthContext";
import { httpGet, httpPost, httpDelete } from "../../../api";

import MDPopup from "../../../components/MDPopup";
import MDTypography from "../../../components/MDTypography";
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDInput from "../../../components/MDInput";

import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";


const Page = (props) => {
  const { user } = useAuthContext();

  const [allDocs, setAllDocs]                     = useState([]);
  const [currentAttachedDoc, setCurrentAttachedDoc] = useState([]);
  const [newAttachedDoc, setNewAttachedDoc]         = useState([]);
  const [toSelectDocs, setToSelectDocs]             = useState([]);
  const [validSubmit, setValidSubmit]               = useState(false);

  // ── NEW: controlled autocomplete value ───────────────────────────────────
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [inputValue, setInputValue]               = useState("");

  // ── Derived state ─────────────────────────────────────────────────────────
  useEffect(() => {
    setValidSubmit(newAttachedDoc.length > 0);
  }, [newAttachedDoc]);

  useEffect(() => {
    const alreadyAttachedIds = currentAttachedDoc.map((d) => d.document_id);
    const alreadyNewIds      = newAttachedDoc.map((d) => d.id);

    setToSelectDocs(
      allDocs.filter(
        (doc) => !alreadyAttachedIds.includes(doc.id) && !alreadyNewIds.includes(doc.id)
      )
    );
  }, [newAttachedDoc, currentAttachedDoc, allDocs]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Called when user picks a suggestion from the autocomplete dropdown
  const handleNewAttached = (event, selectedDoc) => {
    if (!selectedDoc) return;

    setNewAttachedDoc((prev) => [...prev, selectedDoc]);

    // Reset the autocomplete field after selection
    setAutocompleteValue(null);
    setInputValue("");
  };

  const handleDeleteAttached = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    const response = await httpDelete(
      process.env.REACT_APP_URL_BASE + `/document/attach/delete/${id}`
    );

    if (response.status !== 200) {
      alert("Something went wrong");
      console.log(response);
    } else {
      setCurrentAttachedDoc((prev) => prev.filter((doc) => doc.combined_id !== id));
    }
  };

  const handleDeleteNewAttached = (id) => {
    setNewAttachedDoc((prev) => prev.filter((doc) => doc.id !== id));
  };

  // ── Fetch on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAllDocs = async () => {
      const response = await httpGet(process.env.REACT_APP_URL_BASE + `/document/`);
      if (response.status !== 200) {
        alert("Something went wrong");
        console.log(response);
        props.close();
        return;
      }
      setAllDocs(await response.json());
    };

    const fetchCurrentAttachedDoc = async () => {
      const response = await httpGet(
        process.env.REACT_APP_URL_BASE + `/document/tvs/${props.model.id}`
      );
      if (response.status !== 200) {
        alert("Something went wrong");
        console.log(response);
        props.close();
        return;
      }
      setCurrentAttachedDoc(await response.json());
    };

    if (props.open) {
      fetchAllDocs();
      fetchCurrentAttachedDoc();
    }
  }, [props.open]);

  // Reset on close/open
  useEffect(() => {
    setNewAttachedDoc([]);
    setAutocompleteValue(null);
    setInputValue("");
    setValidSubmit(false);
  }, [props.open]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const response = await httpPost(process.env.REACT_APP_URL_BASE + `/document/attach`, {
      tv_id:        props.model.id,
      document_ids: newAttachedDoc.map((doc) => doc.id),
    });

    if (response.status !== 201) {
      alert("Something went wrong");
      console.log(response);
      return;
    }

    props.setRefreshed(true);
    props.close();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <MDPopup
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      open={props.open}
    >
      <MDTypography variant="h3" color="info" textAlign="center" fontWeight="bold">
        Attach Document To This TV Model
      </MDTypography>

      <br />

      {/* ── Attached tags (existing + newly added) ─────────────────────── */}
      <MDBox width="50%">
        {currentAttachedDoc.map((doc) => (
          <MDButton
            key={doc.combined_id}
            variant="outlined"
            color="error"
            sx={{ margin: ".2em" }}
            onClick={() => handleDeleteAttached(doc.combined_id)}
          >
            <Icon fontSize="large">cancel</Icon> {doc.document_name}
          </MDButton>
        ))}

        {newAttachedDoc.map((doc) => (
          <MDButton
            key={doc.id}
            variant="outlined"
            color="text"
            sx={{ margin: ".2em" }}
            onClick={() => handleDeleteNewAttached(doc.id)}
          >
            <Icon fontSize="large">cancel</Icon> {doc.name}
          </MDButton>
        ))}
      </MDBox>

      <br />

      {/* ── Autocomplete input ────────────────────────────────────────── */}
      <MDBox width="50%">
        <Autocomplete
			  value={autocompleteValue}
			  inputValue={inputValue}
			  onChange={handleNewAttached}
			  onInputChange={(e, val) => setInputValue(val)}
			  options={toSelectDocs}
			  getOptionLabel={(option) => option.name ?? ""}
			  isOptionEqualToValue={(option, val) => option.id === val.id}
			  filterOptions={(options, { inputValue: query }) => {
			    const q = query.toLowerCase().trim();
			    return q.length === 0
			      ? []
			      : options.filter((o) => o.name.toLowerCase().includes(q));
			  }}
			  noOptionsText={inputValue.trim().length === 0 ? "Start typing to search…" : "No documents found"}

			  // ── Force dropdown above MDPopup overlay ──────────────────────────────
			  slotProps={{
			    popper: {
			      style: { zIndex: 9999 },
			    },
			  }}
			  // ─────────────────────────────────────────────────────────────────────

			  renderInput={(params) => (
			    <TextField
			      {...params}
			      label="Search document…"
			      variant="outlined"
			      size="small"
			      fullWidth
			    />
			  )}
			/>
      </MDBox>

      <br />

      {/* ── Actions ───────────────────────────────────────────────────── */}
      <MDBox width="70%" textAlign="center">
        <MDButton
          onClick={handleSubmit}
          disabled={!validSubmit}
          variant="text"
          color="success"
        >
          Save
        </MDButton>
        <MDButton variant="outlined" color="error" onClick={props.close}>
          Cancel
        </MDButton>
      </MDBox>
    </MDPopup>
  );
};

export default Page;